import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AutoModel } from "./AutoModel";
import { useShellActions } from "./ShellActionsContext";
import {
  getMembershipCurrentApi,
  type MembershipCurrentRespVO,
} from "../../service/membership";
import { globalStore } from "../../utils/globalStore";
import "./MemberModal.scss";

export interface MemberModalProps {
  open: boolean;
  onClose: () => void;
  /** 会员方案 fallback（与 AccountMenuPopup 同源；扩展 cycle / status / autoRenew 三个字段） */
  membership?: {
    label: string;
    cycle: string;
    expiredAt: string;
    status: string;
    autoRenew: string;
  };
  /** 是否付费用户 fallback：决定「购买积分包」按钮是否显示（doc 同款规则） */
  isPaid?: boolean;
}

/** 把后端 billingCycle 字符串映射成中文：free 免费 / month 月付 / year 年付 */
const formatCycle = (cycle: string): string => {
  const s = String(cycle || "").trim().toLowerCase();
  if (!s) return "—";
  if (s === "free") return "免费";
  if (s === "year" || /y|annual|年/.test(s)) return "年付";
  if (s === "month" || /m/.test(s)) return "月付";
  return cycle;
};

/** 把后端 status 字符串映射成中文 */
const formatStatus = (status: string): string => {
  const s = String(status || "").trim().toLowerCase();
  if (!s) return "—";
  if (/active|生效|正常/.test(s)) return "生效中";
  if (/expire|过期/.test(s)) return "已过期";
  if (/cancel|取消/.test(s)) return "已取消";
  return status;
};

/** 把日期字符串裁剪为 YYYY.MM.DD 显示 */
const formatDate = (value: string | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
};

export const MemberModal: FC<MemberModalProps> = ({
  open,
  onClose,
  membership: membershipProp,
  isPaid: isPaidProp = true,
}) => {
  const actions = useShellActions();
  const [current, setCurrent] = useState<MembershipCurrentRespVO | null>(
    () => globalStore.getMembership()
  );

  // 每次打开时：若 globalStore 已有快照直接使用，否则异步拉取一次
  useEffect(() => {
    if (!open) return;
    const cached = globalStore.getMembership();
    if (cached) {
      setCurrent(cached);
      return;
    }
    let cancelled = false;
    setCurrent(null);
    getMembershipCurrentApi()
      .then((res) => {
        if (cancelled) return;
        if (res?.code === 0 && res.data) {
          setCurrent(res.data);
          globalStore.setMembership(res.data);
        }
      })
      .catch(() => {
        /* 静默失败：保留 props fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  /**
   * 订阅 globalStore：业务侧支付/购买/积分消耗后调用 triggerMembershipRefresh()，
   * HomeShell 会重新拉取并写回 globalStore.membership，本组件随之同步到最新数据。
   */
  useEffect(() => {
    const unsub = globalStore.subscribe(() => {
      const next = globalStore.getMembership();
      setCurrent(next);
    });
    return unsub;
  }, []);

  /** 方案展示值：优先接口数据 */
  const planLabel = current?.planName || membershipProp?.label || "未开通会员";
  const planCycle = current?.billingCycle ? formatCycle(current.billingCycle) : membershipProp?.cycle || "—";
  const planStatus = current?.status ? formatStatus(current.status) : membershipProp?.status || "—";
  const planExpiredAt = current?.endTime ? formatDate(current.endTime) : membershipProp?.expiredAt || "—";
  /** 自动续费：当前接口未直接给出，保留 prop 或显示 — */
  const planAutoRenew = membershipProp?.autoRenew || "—";
  /** 付费判定：membershipId > 0 即为有生效订阅；否则回落到 prop */
  const displayIsPaid =
    typeof current?.membershipId === "number" && current.membershipId > 0
      ? true
      : isPaidProp;
  /** 积分展示值 */
  const memberPoints = current?.memberAvailablePoint;
  const packBalance = current?.permanentAvailablePoint;

  const handleOpenPointsDetail = () => {
    onClose();
    actions?.openPointsDetail();
  };

  const handleOpenPurchase = () => {
    onClose();
    actions?.openPurchase();
  };

  const navigate = useNavigate();
  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  return (
    <AutoModel
      open={open}
      onClose={onClose}
      title={
        <div>
          <h2>会员与积分</h2>
          <p>查看当前方案、积分余额和有效期</p>
        </div>
      }
      size="md"
      contentMaxWidth={620}
      contentClassName="member-modal-shell"
      footerRight={null}
    >
      <div className="member-modal-body">
        <div className="member-modal-plan">
          <div className="member-modal-plan-top">
            <div>
              <span className="member-modal-plan-label">CURRENT PLAN</span>
              <h3>{planLabel}</h3>
            </div>
            <span className="member-modal-plan-cycle">{planCycle}</span>
          </div>
          <div className="member-modal-plan-meta">
          <div>
              <span>会员状态</span>
              <strong>生效中</strong>
            </div>
            <div>
              <span>订阅时间</span>
              <strong>{current?.startTime ? formatDate(current.startTime) : "—"}</strong>
            </div>
            <div>
              <span>有效期至</span>
              <strong>{planExpiredAt}</strong>
            </div>
          </div>
        </div>

        <div className="member-modal-points">
          <div className="member-modal-points-card">
            <span>本周期会员积分</span>
            <strong>{memberPoints == null ? "—" : memberPoints.toLocaleString("zh-CN")}</strong>
            <small>本周期结束后清零</small>
          </div>
          <div className="member-modal-points-card">
            <span>积分包余额</span>
            <strong>{packBalance == null ? "—" : packBalance.toLocaleString("zh-CN")}</strong>
            <small>积分包余额将于下次购买后自动累加</small>
          </div>
        </div>

        <div className="member-modal-actions">
          <button
            type="button"
            className="member-modal-btn"
            onClick={handleUpgrade}
          >
            升级会员
          </button>
          <button
            type="button"
            className="member-modal-btn"
            onClick={handleOpenPointsDetail}
          >
            查看积分明细
          </button>
          {displayIsPaid ? (
            <button
              type="button"
              className="member-modal-btn member-modal-btn--primary"
              onClick={handleOpenPurchase}
            >
              购买积分包
            </button>
          ) : null}
        </div>
      </div>
    </AutoModel>
  );
};